import * as React from 'react';
import { XYChart, Tooltip, AxisBottom, AxisLeft, Bar, Grid } from '../xychart';
import * as BarGraph from './types/bar-graph';
import { useGetLeftAxisLabelOffset } from './utils/use-calculate-left-axis-label-offset';

const NUM_X_TICKS = 2;

export function MasonryBarGraph<Datum>({
  data,
  accessors,
  title,
  description,
  axisLeftLabel,
  axisBottomLabel,
  ariaDatumLabel,
  renderTooltip,
  barPadding,
  barRadius,
  yAxisMax,
  xAxisTicks,
  xAxisLabelTicks,
  xAxisTickFormat,
  datumsPerColumn: datumsPerColumnProp,
}: BarGraph.MasonryBarGraph<Datum>) {
  const yAxisLabelOffset = useGetLeftAxisLabelOffset(yAxisMax);
  const [leftMargin, setLeftMargin] = React.useState(0);
  const [bottomMargin, setBottomMargin] = React.useState(0);
  const datumsPerColumn =
    datumsPerColumnProp ??
    (xAxisTicks?.length ? Math.round(data.length / xAxisTicks.length) : 1);

  return (
    <XYChart
      title={title}
      desc={description}
      data={data}
      accessors={accessors}
      margin={{
        left: leftMargin,
        bottom: bottomMargin,
      }}
      xScale={{
        type: 'band',
        paddingInner: barPadding,
        paddingOuter: barPadding / 2,
      }}
      yScale={{
        type: 'linear',
        domain: [0, yAxisMax],
      }}
    >
      <Grid
        rowTickValues={[0, yAxisMax / NUM_X_TICKS, yAxisMax]}
        columnTickValues={xAxisTicks}
        centerColumns
        datumsPerColumn={datumsPerColumn}
      />
      <AxisLeft
        label={axisLeftLabel}
        labelOffset={yAxisLabelOffset}
        tickValues={[0, yAxisMax / NUM_X_TICKS, yAxisMax]}
        onWidthChange={(width) => setLeftMargin(width)}
      />
      <AxisBottom
        label={axisBottomLabel}
        tickValues={xAxisLabelTicks ?? xAxisTicks}
        tickFormat={xAxisTickFormat}
        onHeightChange={(height) => setBottomMargin(height)}
      />
      {data.map((datum, i) => {
        const isLastBar = data.length - 1 === i;

        return (
          <Bar
            key={`bar-${accessors.xAccessor(datum)}:${accessors.yAccessor(
              datum,
            )}`}
            datum={datum}
            accessors={accessors}
            highlight={isLastBar}
            aria-label={ariaDatumLabel(datum)}
            radius={barRadius}
          />
        );
      })}
      <Tooltip<Datum> snapToDatum>{renderTooltip}</Tooltip>
    </XYChart>
  );
}
